/**
 * Ein fremdes Reel abspielen — über den offiziellen Player der Plattform.
 *
 * Nie selbst herunterladen und ausliefern: Das Video gehört dem Konto, die
 * Plattformen erlauben das Einbetten, nicht das Kopieren. Und weil ein
 * eingebetteter Player Daten an Meta bzw. TikTok schickt, lädt er erst nach
 * einem Klick — einmal je Browser, jederzeit widerrufbar (`useEinbetten`).
 */

import { useCallback, useEffect, useState } from "react";

const SCHLUESSEL = "tc-einbetten";
const EREIGNIS = "tc-einbetten";

/** Für Browser ohne Speicher: dann gilt die Freigabe bis zum Neuladen. */
let sitzung = false;

function lesen(): boolean {
  try {
    return window.localStorage.getItem(SCHLUESSEL) === "ja" || sitzung;
  } catch {
    return sitzung;
  }
}

/** Ob Videos von Instagram/TikTok geladen werden dürfen — gilt für alle Player der Seite. */
export function useEinbetten() {
  const [an, setAn] = useState(false);

  useEffect(() => {
    setAn(lesen());
    const neu = () => setAn(lesen());
    window.addEventListener(EREIGNIS, neu);
    return () => window.removeEventListener(EREIGNIS, neu);
  }, []);

  const setzen = useCallback((wert: boolean) => {
    sitzung = wert;
    try {
      if (wert) window.localStorage.setItem(SCHLUESSEL, "ja");
      else window.localStorage.removeItem(SCHLUESSEL);
    } catch {
      /* ohne Speicher gilt die Freigabe nur für diese Seite */
    }
    setAn(wert);
    window.dispatchEvent(new Event(EREIGNIS));
  }, []);

  const erlauben = useCallback(() => setzen(true), [setzen]);
  const widerrufen = useCallback(() => setzen(false), [setzen]);
  return { an, erlauben, widerrufen };
}

const PLATTFORM: Record<string, string> = { instagram: "Instagram", tiktok: "TikTok" };

export interface ReelPlayerProps {
  /** `embed_url` aus der Engine. Leer: kein Player möglich. */
  src?: string;
  platform: string;
  titel?: string;
}

export function ReelPlayer({ src, platform, titel }: ReelPlayerProps) {
  const { an, erlauben } = useEinbetten();
  const name = PLATTFORM[platform] ?? platform;

  if (!src) {
    return (
      <div className="ch-player ch-player-leer">
        <span className="ch-klein">Kein Player verfügbar</span>
      </div>
    );
  }
  if (!an) {
    return (
      <div className="ch-player ch-player-leer">
        <span>{name}</span>
        <button type="button" className="ch-knopf ch-knopf-primaer" onClick={erlauben}>
          Videos laden
        </button>
        <span className="ch-klein">
          Lädt die Videos direkt von Instagram und TikTok — dabei gehen Daten an Meta bzw. TikTok.
        </span>
      </div>
    );
  }
  return (
    <div className="ch-player">
      <iframe
        src={src}
        title={titel ? `${name}: ${titel}` : `${name}-Video`}
        loading="lazy"
        allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        scrolling="no"
      />
    </div>
  );
}
